import React, { FC, useEffect, useState } from "react";

import { DataViewKey } from "@keepeek/commons";
import { BaseComponentProps } from "@keepeek/refront-components";
import { AppBar, Box, Tab, Tabs, Tooltip } from "@mui/material";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { TranslationSchema } from "../../../models/configuration/components/tabsWidget";
import { Template, Type } from "../../../models/configuration/definitions/template";
import { Theme } from "../../../models/configuration/theme";
import { backContextState } from "../../../providers/backContext/atoms";
import { useConfiguration } from "../../../providers/config/hooks";
import { WidgetStateReturn, widgetStatesAtom, WidgetUIState } from "../atoms";
import KTemplates from "../KTemplates";
import { KWidgetWithElementProps, WidgetConfigurations } from "../models";
import { getWidgetsKeysTemplates } from "../utils/getWidgetsKeysTemplates";
import { injectElementIdInConfigurationIfNeeded } from "../utils/injectElementIdInConfigurationIfNeeded";
import { isDisplayedTab, isWidgetsRendered } from "../utils/isWidgetsRendered";

export type KTab = {
  translations: TranslationSchema[];
  widgetsConfiguration: WidgetConfigurations | null;
  templatesConfiguration: Template[];
  textOnHover: TranslationSchema[];
};

export type KTabsWidgetProps = {
  tabs: KTab[];
} & KWidgetWithElementProps &
  BaseComponentProps;

const KTabsWidget: FC<React.PropsWithChildren<KTabsWidgetProps>> = function ({
  elementId,
  tabs,
  className,
  testId = "KTabsWidget",
}) {
  const backContext = useRecoilValue(backContextState);
  const { t } = useTranslation();
  const [value, setValue] = useState<number>(0);
  const [activeTabUpdatedByUser, setActiveTabUpdatedByUser] = useState<boolean>(false);
  const themeConfiguration = useConfiguration<Theme>(CONFIGURATION_SECTION_PATH.THEME);
  const { locale } = useRouter();
  const widgetStates = useRecoilValue(widgetStatesAtom);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    if (widgetStates.some((ws) => ws.state === WidgetUIState.LOADING)) {
      setActiveTabUpdatedByUser(true);
    }
    setValue(newValue);
  };

  useEffect(() => {
    setValue(0);
    setActiveTabUpdatedByUser(false);
  }, [elementId]);

  useEffect(() => {
    const defaultTab = tabs.findIndex((value) => isDisplayedTab(value, widgetStates));
    if (widgetStates.some((ws) => ws.state === WidgetUIState.LOADING)) {
      // While all tabs are not rendered or null, change active tab to the first rendered tab
      if (!activeTabUpdatedByUser && defaultTab >= 0) {
        setValue(defaultTab);
      }
      return;
    }

    // When all tabs are rendered, select first tab with at least one rendered widget to avoid no actif tab
    if (!activeTabUpdatedByUser && defaultTab >= 0) {
      setValue(defaultTab);
    }
  }, [activeTabUpdatedByUser, tabs, widgetStates]);

  if (!tabs) {
    return null;
  }

  function getTotalCount(widgets: { id: string; type: Type }[]): number {
    let totalCount: number = 0;
    widgets.forEach((ws) => {
      const widget = widgetStates.find(
        (w) =>
          (w.type === Type.Attachments && ws.type === Type.Attachments) ||
          (w.type === Type.Sheet && ws.type === Type.Sheet) ||
          (w.id === ws.id && w.type == ws.type),
      );
      if (widget && widget.elementCount) {
        totalCount += widget.elementCount;
      }
    });
    return totalCount;
  }

  return (
    <Box
      className={clsx("KTabsWidget", className)}
      data-testid={testId}
      sx={{
        flexGrow: 1,
        width: "100%",
        zIndex: (theme) => theme.zIndex.appBar - 1, // let Popper menu be visible above the tabs
      }}
    >
      <AppBar position={"sticky"} color={"default"}>
        <Tabs
          value={isTabReadyForSelected(value, tabs, widgetStates) ? value : false}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          aria-label={t("components.tabs.aria.label") as string}
          className="KTabsWidget-tabs"
        >
          {tabs.map((tab, index) => {
            const widgetsKey = getWidgetsKeysTemplates(tab.templatesConfiguration);
            const isDisplayedValue = isWidgetsRendered(widgetsKey, widgetStates);
            const totalCount = getTotalCount(widgetsKey);
            const label = `${tab.translations.find((t) => t.language === locale)?.value || ""} ${
              totalCount > 0 ? `(${totalCount})` : ""
            }`;
            const textOnHover = `${
              tab.textOnHover.find((t) => t.language === locale)?.value || ""
            }`;
            return (
              <Tooltip key={index} title={textOnHover}>
                <Tab
                  key={index}
                  label={label}
                  style={{ display: isDisplayedValue ? "block" : "none" }}
                  {...a11yProps(index)}
                  disabled={!isDisplayedValue}
                  data-testid={`scrollable-auto-tabpanel-${
                    tab.translations.find((t) => t.language === locale)?.value
                  }`}
                />
              </Tooltip>
            );
          })}
        </Tabs>
      </AppBar>
      {tabs.map((tab, index) => (
        <KTabPanel
          key={index}
          value={value}
          index={index}
          /* Component must be loaded to kwnow if there are datas in it to display. Else it will be hidden */
          sx={{ display: value === index ? "block" : "none" }}
        >
          <KTemplates
            templatesConfiguration={tab.templatesConfiguration}
            widgetsConfiguration={
              injectElementIdInConfigurationIfNeeded({
                elementId: elementId || undefined,
                widgetsConfiguration: tab.widgetsConfiguration || undefined,
              }) || null
            }
            themeConfiguration={themeConfiguration || null}
            dataViewKey={backContext.key || DataViewKey.SEARCH}
          />
        </KTabPanel>
      ))}
    </Box>
  );
};

const KTabPanel: FC<
  React.PropsWithChildren<
    {
      value: number;
      index: number;
    } & BaseComponentProps
  >
> = function ({ children, index, className, testId = `KTabPanel-${index}`, ...rest }) {
  return (
    <Box
      role="tabpanel"
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...rest}
      className={clsx("KTabPanel", className)}
      data-testid={testId}
    >
      {children}
    </Box>
  );
};

function a11yProps(index: number) {
  return {
    id: `scrollable-auto-tab-${index}`,
    "aria-controls": `scrollable-auto-tabpanel-${index}`,
  };
}

const isTabReadyForSelected = (
  tabValue: number,
  tabs: KTab[],
  widgetStates: WidgetStateReturn[],
): boolean => {
  if (tabValue === undefined || tabValue === null) {
    return false;
  }
  const tab = tabs[tabValue];
  if (!tab) {
    return false;
  }
  const widgetsKey = getWidgetsKeysTemplates(tab.templatesConfiguration);
  return isWidgetsRendered(widgetsKey, widgetStates);
};

export default KTabsWidget;
